import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect } from 'react';

// Action types for state management
const ACTIONS = {
  // Essay management
  SET_ESSAYS: 'SET_ESSAYS',
  ADD_ESSAY: 'ADD_ESSAY',
  UPDATE_ESSAY: 'UPDATE_ESSAY',
  DELETE_ESSAY: 'DELETE_ESSAY',
  SELECT_ESSAY: 'SELECT_ESSAY',
  
  // UI state management
  SET_MODE: 'SET_MODE',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  
  // Assessment state
  SET_ASSESSMENT_DATA: 'SET_ASSESSMENT_DATA',
  SET_STRATEGIC_INSIGHTS: 'SET_STRATEGIC_INSIGHTS',
  
  // Progress tracking
  SET_PROGRESS: 'SET_PROGRESS',
  UPDATE_STEP: 'UPDATE_STEP',
  
  // Batch operations
  START_BATCH_OPERATION: 'START_BATCH_OPERATION',
  END_BATCH_OPERATION: 'END_BATCH_OPERATION',
  UPDATE_BATCH_PROGRESS: 'UPDATE_BATCH_PROGRESS'
};

// Initial state
const initialState = {
  // Essay data
  essays: [],
  selectedEssay: null,
  
  // UI state
  currentMode: 'freewrite', // 'selection' | 'writing' | 'builder' | 'freewrite' | 'assessment' - Default to freewrite
  isLoading: false,
  error: null,
  
  // Assessment data
  assessmentData: null,
  strategicInsights: null,
  
  // Progress tracking
  progress: {
    currentStep: 0,
    totalSteps: 0,
    stepProgress: {}
  },
  
  // Batch operations
  batchOperation: {
    isActive: false,
    type: null,
    progress: 0,
    total: 0,
    results: []
  }
};

// Reducer function
function essayDevelopmentReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_ESSAYS:
      return {
        ...state,
        essays: action.payload,
        error: null
      };
      
    case ACTIONS.ADD_ESSAY:
      return {
        ...state,
        essays: [...state.essays, action.payload],
        selectedEssay: action.payload,
        error: null
      };
      
    case ACTIONS.UPDATE_ESSAY:
      return {
        ...state,
        essays: state.essays.map(essay =>
          essay.id === action.payload.id
            ? { ...essay, ...action.payload.updates }
            : essay
        ),
        selectedEssay: state.selectedEssay?.id === action.payload.id
          ? { ...state.selectedEssay, ...action.payload.updates }
          : state.selectedEssay,
        error: null
      };
      
    case ACTIONS.DELETE_ESSAY:
      return {
        ...state,
        essays: state.essays.filter(essay => essay.id !== action.payload),
        selectedEssay: state.selectedEssay?.id === action.payload ? null : state.selectedEssay,
        error: null
      };
      
    case ACTIONS.SELECT_ESSAY:
      return {
        ...state,
        selectedEssay: action.payload
      };
      
    case ACTIONS.SET_MODE:
      return {
        ...state,
        currentMode: action.payload
      };
      
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
      
    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };
      
    case ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
      
    case ACTIONS.SET_ASSESSMENT_DATA:
      return {
        ...state,
        assessmentData: action.payload
      };
      
    case ACTIONS.SET_STRATEGIC_INSIGHTS:
      return {
        ...state,
        strategicInsights: action.payload
      };
      
    case ACTIONS.SET_PROGRESS:
      return {
        ...state,
        progress: action.payload
      };
      
    case ACTIONS.UPDATE_STEP:
      return {
        ...state,
        progress: {
          ...state.progress,
          currentStep: action.payload.step,
          stepProgress: {
            ...state.progress.stepProgress,
            [action.payload.step]: action.payload.progress
          }
        }
      };
      
    case ACTIONS.START_BATCH_OPERATION:
      return {
        ...state,
        batchOperation: {
          isActive: true,
          type: action.payload.type,
          progress: 0,
          total: action.payload.total,
          results: []
        }
      };
      
    case ACTIONS.UPDATE_BATCH_PROGRESS:
      return {
        ...state,
        batchOperation: {
          ...state.batchOperation,
          progress: action.payload.progress,
          results: action.payload.results || state.batchOperation.results
        }
      };
      
    case ACTIONS.END_BATCH_OPERATION:
      return {
        ...state,
        batchOperation: {
          isActive: false,
          type: null,
          progress: 0,
          total: 0,
          results: action.payload?.results || []
        }
      };
      
    default:
      return state;
  }
}

// Create context
const EssayDevelopmentContext = createContext();

// Provider component
export const EssayDevelopmentProvider = ({ children }) => {
  const [state, dispatch] = useReducer(essayDevelopmentReducer, initialState);
  
  // Timeout management for batch operations
  const batchTimeoutsRef = useRef(new Map());
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      batchTimeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      batchTimeoutsRef.current.clear();
    };
  }, []);
  
  // Essay management actions
  const setEssays = useCallback((essays) => {
    dispatch({ type: ACTIONS.SET_ESSAYS, payload: essays });
  }, []);
  
  const addEssay = useCallback((essay) => {
    dispatch({ type: ACTIONS.ADD_ESSAY, payload: essay });
  }, []);
  
  const updateEssay = useCallback((id, updates) => {
    dispatch({ type: ACTIONS.UPDATE_ESSAY, payload: { id, updates } });
  }, []);
  
  const deleteEssay = useCallback((id) => {
    dispatch({ type: ACTIONS.DELETE_ESSAY, payload: id });
  }, []);
  
  const selectEssay = useCallback((essay) => {
    dispatch({ type: ACTIONS.SELECT_ESSAY, payload: essay });
  }, []);
  
  // UI state actions
  const setMode = useCallback((mode) => {
    dispatch({ type: ACTIONS.SET_MODE, payload: mode });
  }, []);
  
  const setLoading = useCallback((isLoading) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: isLoading });
  }, []);
  
  const setError = useCallback((error) => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: error });
  }, []);
  
  const clearError = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_ERROR });
  }, []);
  
  // Assessment actions
  const setAssessmentData = useCallback((data) => {
    dispatch({ type: ACTIONS.SET_ASSESSMENT_DATA, payload: data });
  }, []);
  
  const setStrategicInsights = useCallback((insights) => {
    dispatch({ type: ACTIONS.SET_STRATEGIC_INSIGHTS, payload: insights });
  }, []);
  
  // Progress tracking actions
  const setProgress = useCallback((progress) => {
    dispatch({ type: ACTIONS.SET_PROGRESS, payload: progress });
  }, []);
  
  const updateStep = useCallback((step, progress) => {
    dispatch({ type: ACTIONS.UPDATE_STEP, payload: { step, progress } });
  }, []);
  
  // Batch operation management with proper timeout handling
  const startBatchOperation = useCallback((type, total) => {
    dispatch({ type: ACTIONS.START_BATCH_OPERATION, payload: { type, total } });
  }, []);
  
  const updateBatchProgress = useCallback((progress, results) => {
    dispatch({ type: ACTIONS.UPDATE_BATCH_PROGRESS, payload: { progress, results } });
  }, []);
  
  const endBatchOperation = useCallback((results) => {
    dispatch({ type: ACTIONS.END_BATCH_OPERATION, payload: { results } });
    // Clear any remaining batch timeouts
    batchTimeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
    batchTimeoutsRef.current.clear();
  }, []);
  
  // Safe batch essay creation with proper throttling
  const addEssaysBatch = useCallback(async (essayDataArray) => {
    if (!Array.isArray(essayDataArray) || essayDataArray.length === 0) {
      return [];
    }
    
    startBatchOperation('essay_creation', essayDataArray.length);
    
    const batchSize = 3; // Process 3 at a time to prevent memory issues
    const results = [];
    
    try {
      for (let i = 0; i < essayDataArray.length; i += batchSize) {
        const batch = essayDataArray.slice(i, i + batchSize);
        
        // Process batch
        const batchPromises = batch.map(async (essayData, batchIndex) => {
          try {
            // Simulate essay creation (replace with actual API call)
            return new Promise((resolve) => {
              const timeoutKey = `batch_${i}_${batchIndex}`;
              const timeoutId = setTimeout(() => {
                const newEssay = {
                  id: `essay_${Date.now()}_${i}_${batchIndex}`,
                  ...essayData,
                  created_at: new Date().toISOString()
                };
                batchTimeoutsRef.current.delete(timeoutKey);
                resolve(newEssay);
              }, 100); // Short delay to prevent overwhelming
              
              batchTimeoutsRef.current.set(timeoutKey, timeoutId);
            });
          } catch (error) {
            console.error(`Error creating essay in batch:`, error);
            return null;
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        const validResults = batchResults.filter(result => result !== null);
        results.push(...validResults);
        
        // Update progress
        updateBatchProgress(results.length, results);
        
        // Add successful essays to state
        validResults.forEach(essay => addEssay(essay));
        
        // Small delay between batches to prevent memory issues
        if (i + batchSize < essayDataArray.length) {
          await new Promise(resolve => {
            const timeoutKey = `batch_delay_${i}`;
            const timeoutId = setTimeout(() => {
              batchTimeoutsRef.current.delete(timeoutKey);
              resolve();
            }, 200);
            batchTimeoutsRef.current.set(timeoutKey, timeoutId);
          });
        }
      }
      
      endBatchOperation(results);
      return results;
      
    } catch (error) {
      setError(`Batch essay creation failed: ${error.message}`);
      endBatchOperation(results);
      return results;
    }
  }, [startBatchOperation, updateBatchProgress, endBatchOperation, addEssay, setError]);
  
  // Context value
  const value = {
    // State
    ...state,
    
    // Essay actions
    setEssays,
    addEssay,
    updateEssay,
    deleteEssay,
    selectEssay,
    addEssaysBatch,
    
    // UI actions
    setMode,
    setLoading,
    setError,
    clearError,
    
    // Assessment actions
    setAssessmentData,
    setStrategicInsights,
    
    // Progress actions
    setProgress,
    updateStep,
    
    // Batch operation actions
    startBatchOperation,
    updateBatchProgress,
    endBatchOperation
  };
  
  return (
    <EssayDevelopmentContext.Provider value={value}>
      {children}
    </EssayDevelopmentContext.Provider>
  );
};

// Custom hook to use the context
export const useEssayDevelopment = () => {
  const context = useContext(EssayDevelopmentContext);
  
  if (!context) {
    throw new Error('useEssayDevelopment must be used within an EssayDevelopmentProvider');
  }
  
  return context;
};

export default EssayDevelopmentContext;